import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';

const Navbar = () => {
    const { user, logout } = useAuth();
    const { get, post } = useApi();
    const navigate = useNavigate();
    const location = useLocation();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    
    const userMenuRef = useRef(null);
    const notificationRef = useRef(null);
    const mobileMenuRef = useRef(null);

    // Click outside detection
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setIsNotificationOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
                setIsMobileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const displayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Guest' : 'Guest';
    const initials = displayName
        .split(' ')
        .map((token) => token.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const { courses, is_class_teacher, is_hod, is_head_of_studies, is_principal } = useAppState();

    const primaryLinks = useMemo(
        () => {
            const links = [];
            if (!(user?.role === 'teacher' && (!courses || courses.length === 0))) {
                links.push({ to: '/dashboard', label: 'Dashboard' });
            }

            if (user?.role === 'teacher' && is_class_teacher) {
                links.push({ to: '/teacher/class-dashboard', label: 'Form Mistress' });
            }
            if (user?.role === 'teacher' && is_hod) {
                links.push({ to: '/teacher/hod-dashboard', label: is_principal ? 'Principal' : 'Department management' });
            }
            if (user?.role === 'teacher' && is_head_of_studies) {
                links.push({ to: '/teacher/hos-dashboard', label: 'Head of Studies' });
            }
            if (user?.role === 'parent') {
                links.push({ to: '/parent/events', label: 'Events & communications' });
            }
            
            links.push({ to: '/contact', label: 'Contact' });
            return links;
        },
        [user, is_class_teacher, is_hod, is_head_of_studies, is_principal, courses]
    );

    const quickLinks = useMemo(() => {
        const role = user?.role || 'student';
        const isHodUser = is_hod || is_head_of_studies || is_principal;
        const isHodContext = isHodUser || location.pathname.startsWith('/teacher/hod') || location.pathname.startsWith('/teacher/hos');
        const shared = [
            { to: '/settings', label: 'My Account', icon: 'fa-user-gear', roles: ['student', 'teacher', 'parent'] },
        ];
        const studentLinks = [
            { to: '/courses', label: 'Learning Areas', icon: 'fa-layer-group', roles: ['student'] },
            { to: '/success-hub', label: 'Success Hub', icon: 'fa-graduation-cap', roles: ['student'] },
        ];
        const teacherLinks = [
            ...(isHodContext ? [] : [
                { to: '/teacher/courses', label: 'My Learning Areas', icon: 'fa-layer-group', roles: ['teacher'] },
                { to: '/teacher/lesson-plans/templates', label: 'Lesson Templates', icon: 'fa-file-signature', roles: ['teacher'] },
            ]),
            { to: '/teacher/discipline', label: 'Character Registry', icon: 'fa-scale-balanced', roles: ['teacher'] },
        ];
        const parentLinks = [
            { to: '/parent/events', label: 'Events & communications', icon: 'fa-calendar-days', roles: ['parent'], hideOnDesktop: true },
            { to: '/parent/discipline', label: 'Character', icon: 'fa-scale-balanced', roles: ['parent'] },
        ];
        return [...shared, ...(role === 'teacher' ? teacherLinks : role === 'parent' ? parentLinks : studentLinks)].filter((link) =>
            link.roles.includes(role)
        );
    }, [user, location.pathname, is_hod, is_head_of_studies, is_principal]);

    const unreadCount = useMemo(() => notifications.filter((notification) => !notification.is_read).length, [notifications]);

    useEffect(() => {
        setIsNotificationOpen(false);
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;

        const fetchNotifications = async () => {
            try {
                const data = await get('/api/notifications/?unread=true');
                if (!cancelled && Array.isArray(data)) {
                    setNotifications(data.slice(0, 6));
                }
            } catch (err) {
                if (!cancelled) {
                    setNotifications([]);
                }
            }
        };

        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [user, get]);

    const markAllAsRead = async () => {
        // Optimistic UI update - clear notifications
        const previousNotifications = [...notifications];
        setNotifications([]);

        try {
            await post('/api/notifications/mark-all-read/');
        } catch (err) {
            console.error('Error marking as read:', err);
            // Revert on error
            setNotifications(previousNotifications);
        }
    };

    if (['/login', '/signup'].includes(location.pathname) || location.pathname.startsWith('/admin')) {
        return null;
    }

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleNavigate = (path) => {
        setIsMobileMenuOpen(false);
        setIsUserMenuOpen(false);
        navigate(path);
    };

    const toggleUserMenu = () => {
        setIsUserMenuOpen((prev) => !prev);
    };

    return (
        <nav className="bg-white/90 backdrop-blur border-b border-gray-200 sticky top-0 z-[200]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center space-x-6">
                        <Link to="/dashboard" className="flex items-center gap-5 group">
                            <img src="/kianda-school-logo-removebg-preview.png" alt="Kianda LMS" className="h-10 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-110" />
                            <div className="flex flex-col">
                                <span className="text-xl font-black text-[#18216D] uppercase tracking-tighter leading-none">Kianda School</span>
                                <span className="text-[11px] font-bold text-[#FFC425] uppercase tracking-[0.3em] leading-none mt-2">LMS</span>
                            </div>
                        </Link>
                        <div className="hidden md:flex items-center space-x-2">
                            {primaryLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${location.pathname === link.to
                                        ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/20'
                                        : 'text-slate-500 hover:text-[#18216D] hover:bg-slate-50'
                                        }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="hidden md:flex items-center space-x-3">
                        <div className="relative" ref={notificationRef}>
                            <button
                                className="relative p-2 rounded-full hover:bg-gray-100 text-gray-600 focus:outline-none"
                                onClick={() => setIsNotificationOpen((prev) => !prev)}
                                aria-label="Notifications"
                            >
                                <i className="fas fa-bell" aria-hidden="true"></i>
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-semibold rounded-full px-1">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                            {isNotificationOpen && (
                                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-20">
                                    <div className="px-5 py-4 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                                        <div className="flex items-center gap-2">
                                            <p className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">Notifications</p>
                                            {unreadCount > 0 && (
                                                <span className="bg-red-100 text-red-600 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                                                    {unreadCount}
                                                </span>
                                            )}
                                        </div>
                                        {notifications.length > 0 && (
                                            <button
                                                onClick={markAllAsRead}
                                                className="text-[9px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-widest flex items-center gap-1 transition-colors"
                                            >
                                                <span>Mark all read</span>
                                                <i className="fas fa-check-double text-[8px]" />
                                            </button>
                                        )}
                                    </div>
                                    <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                                        {notifications.length === 0 && (
                                            <li className="px-4 py-6 text-center text-sm text-gray-500">No notifications yet.</li>
                                        )}
                                        {notifications.map((notification) => (
                                            <li key={notification.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                                                <p className="text-sm font-medium text-gray-900">{notification.title || 'Notification'}</p>
                                                <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{notification.message}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={toggleUserMenu}
                                className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:border-gray-300 transition-colors"
                                aria-expanded={isUserMenuOpen}
                                aria-label="User menu"
                            >
                                <span className="h-9 w-9 rounded-full bg-[#18216D] text-white flex items-center justify-center text-sm font-black shadow-lg shadow-indigo-900/10">
                                    {initials || 'U'}
                                </span>
                                <div className="text-left">
                                    <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                                    <p className="text-xs text-gray-500 capitalize">{is_principal ? 'Principal' : is_head_of_studies ? 'Head of Studies' : is_hod ? 'HOD' : is_class_teacher ? 'Class Teacher' : (user?.role || 'student')}</p>
                                </div>
                                <i className={`fas fa-chevron-${isUserMenuOpen ? 'up' : 'down'} text-xs text-gray-500`} aria-hidden="true"></i>
                            </button>
                            {isUserMenuOpen && (
                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-20">
                                    <div className="px-5 py-4 border-b border-gray-100 bg-slate-50/50">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Authenticated as</p>
                                        <p className="text-sm font-black text-[#18216D] capitalize">{is_principal ? 'Principal' : is_head_of_studies ? 'Head of Studies' : is_hod ? 'Head of Department (HOD)' : is_class_teacher ? 'Form Mistress' : (user?.role || 'User')}</p>
                                    </div>
                                    <ul className="divide-y divide-gray-50" data-testid="quick-link-list">
                                        {quickLinks.map((link) => (
                                            <li key={link.to} className={link.hideOnDesktop ? "md:hidden" : ""}>
                                                <button
                                                    className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-slate-50 transition-all border-l-4 border-transparent hover:border-[#FFC425] group"
                                                    onClick={() => handleNavigate(link.to)}
                                                >
                                                    <span className="h-10 w-10 rounded-xl bg-[#18216D]/5 text-[#18216D] flex items-center justify-center group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-sm">
                                                        <i className={`fas ${link.icon}`} aria-hidden="true"></i>
                                                    </span>
                                                    <div>
                                                        <p className="text-sm font-black text-gray-900 group-hover:text-[#18216D] transition-colors">{link.label}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Quick Access</p>
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                        <li>
                                            <button
                                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors text-red-600"
                                                onClick={handleLogout}
                                            >
                                                <span className="h-8 w-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                                                    <i className="fas fa-arrow-right-from-bracket" aria-hidden="true"></i>
                                                </span>
                                                <div>
                                                    <p className="text-sm font-semibold">Logout</p>
                                                    <p className="text-xs text-red-400">Sign out securely</p>
                                                </div>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="md:hidden relative" ref={mobileMenuRef}>
                        {/* Mobile: Profile Icon Button replaces Hamburger */}
                        <button
                            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                            className="p-1 rounded-full border border-gray-200"
                            aria-label="Toggle profile menu"
                        >
                            <span className="h-9 w-9 rounded-full bg-[#18216D] text-white flex items-center justify-center text-sm font-black shadow-lg shadow-indigo-900/10">
                                {initials || 'U'}
                            </span>
                        </button>
                        {/* Mobile Profile Dropdown (Replaces Full Menu) */}
                        {isMobileMenuOpen && (
                            <div className="absolute top-16 right-0 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                                <div className="px-5 py-4 border-b border-gray-100 bg-slate-50/50">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="h-10 w-10 rounded-full bg-[#18216D] text-white flex items-center justify-center text-lg font-black">
                                {initials || 'U'}
                            </span>
                            <div>
                                <p className="text-sm font-black text-[#18216D]">{displayName}</p>
                                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{is_principal ? 'Principal' : is_head_of_studies ? 'Head of Studies' : is_hod ? 'Head of Department' : is_class_teacher ? 'Form Mistress' : (user?.role || 'Guest')}</p>
                            </div>
                        </div>
                    </div>
                    <ul className="divide-y divide-gray-50">
                        {/* Profile Link */}
                        <li>
                            <button
                                onClick={() => handleNavigate('/profile')}
                                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50"
                            >
                                <i className="fas fa-user-circle text-slate-400 w-5 text-center"></i>
                                <span className="text-sm font-bold text-gray-700">Profile</span>
                            </button>
                        </li>

                        {/* Dynamic Quick Links (Events, Learning Areas, etc.) */}
                        {quickLinks.filter(link => link.to !== '/settings').map(link => (
                            <li key={link.to}>
                                <button
                                    onClick={() => handleNavigate(link.to)}
                                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50"
                                >
                                    <i className={`fas ${link.icon} text-slate-400 w-5 text-center`}></i>
                                    <span className="text-sm font-bold text-gray-700">{link.label}</span>
                                </button>
                            </li>
                        ))}

                        {/* Class Teacher Link (Class Teachers Only) */}
                        {user?.role === 'teacher' && is_class_teacher && (
                            <li>
                                <button
                                    onClick={() => handleNavigate('/teacher/class-dashboard')}
                                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50"
                                >
                                    <i className="fas fa-users-gear text-slate-400 w-5 text-center"></i>
                                    <span className="text-sm font-bold text-gray-700">Form Mistress</span>
                                </button>
                            </li>
                        )}
                        {user?.role === 'teacher' && is_hod && (
                            <li>
                                <button
                                    onClick={() => handleNavigate('/teacher/hod-dashboard')}
                                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50"
                                >
                                    <i className="fas fa-school text-slate-400 w-5 text-center"></i>
                                    <span className="text-sm font-bold text-gray-700">Department management</span>
                                </button>
                            </li>
                        )}
                        {user?.role === 'teacher' && is_head_of_studies && (
                            <li>
                                <button
                                    onClick={() => handleNavigate('/teacher/hos-dashboard')}
                                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50"
                                >
                                    <i className="fas fa-landmark text-slate-400 w-5 text-center"></i>
                                    <span className="text-sm font-bold text-gray-700">Head of Studies</span>
                                </button>
                            </li>
                        )}
                        {/* Logout Link */}
                        <li>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-red-50 text-red-600"
                            >
                                <i className="fas fa-arrow-right-from-bracket w-5 text-center"></i>
                                <span className="text-sm font-bold">Logout</span>
                            </button>
                        </li>
                    </ul>
                </div>
            )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;

